/**
 * `persistentHighlightData.ts` — web port of Unity
 * `Models/Data/PersistentHighlightData.cs`, together with the visual-state math
 * from `Models/PersistentEntityHighlightModelBase.cs` and the desktop tuning
 * constants from `CampfireScenes/.../PersistentEntityHighlightDesktopModel.cs`.
 *
 * The payload uses Unity's JsonUtility string shape. Web reads legacy
 * `PersistentHighlightJson` snapshot attributes and compatible peer payloads,
 * but treats highlights as runtime state and never writes that DynamoDB
 * attribute:
 *
 *   {"enabled":true,"semantic":"hazard","colorHex":"","pulseMode":"continuous",
 *    "pulseCount":-1,"thicknessMode":"thick"}
 *
 * Contract facts kept verbatim (`PersistentHighlightData.cs`):
 *   - `Serialize` returns null (attribute omitted / cleared) when the highlight
 *     is disabled — "no highlight" is the ABSENCE of the payload.
 *   - `Deserialize` returns null for blank/invalid/disabled JSON and normalizes
 *     every field through the alias tables below.
 *   - `pulseCount` is persisted as the RAW int: `-1` means infinite, and any
 *     value `<= 0` normalizes to `-1`.
 *   - `colorHex` is persisted normalized to `#RRGGBB` (uppercase, alpha
 *     dropped), and JsonUtility writes a null string as `""`.
 *
 * Colors are DISPLAY values, not light-linear ones: Unity stores its `Color32`
 * semantics in gamma space and shows them as-is, so `resolveHighlightColor`
 * returns raw 0..1 components meant for `Color.setRGB` with no conversion.
 * See `runtime/engine/COLOR_PIPELINE.md` — the outline colors are Population B.
 *
 * ⚠ WEB DEVIATIONS — the "Part Highlight (outline + inner glow)" design handoff
 * (2026-09-15) replaced the Unity desktop LOOK, while the persisted contract
 * above stays Unity's:
 *   - The semantic palette is the design's (`highlight/default` #FFD233,
 *     `highlight/success` #2E9E54, `highlight/alert` #F84536), not Unity's
 *     `Color32` values.
 *   - The pulse animates OPACITY on a 2800 ms cycle (glow 20→72→20 %, outline
 *     80→100→80 %, eased `cubic-bezier(.4,0,.4,1)` per half) instead of Unity's
 *     0.8 Hz thickness pulse; steady is glow 55 % / outline 100 %. So a
 *     `pulseCount` highlight runs `pulseCount × pulseMs` here, and a desktop
 *     peer in the same session may clear it sooner (whichever client expires
 *     first persists the clear).
 *   - Three OPTIONAL web-only fields ride the same JSON — `pulseMs`,
 *     `outlineWidth` (CSS px), `glowReach` (CSS px) — written only when set, so
 *     an unset highlight serializes byte-identically to Unity's. JsonUtility
 *     ignores them on read; a desktop REWRITE drops them and the web falls
 *     back to the `thicknessMode` preset, which is the intended degradation.
 */

export const PersistentHighlightSemantics = {
  Attention: "attention",
  Correct: "correct",
  Hazard: "hazard",
} as const;

export const PersistentHighlightPulseModes = {
  SingleSoft: "singleSoft",
  Continuous: "continuous",
  None: "none",
} as const;

export const PersistentHighlightThicknessModes = {
  Thin: "thin",
  Medium: "medium",
  Thick: "thick",
} as const;

export type PersistentHighlightSemantic =
  (typeof PersistentHighlightSemantics)[keyof typeof PersistentHighlightSemantics];
export type PersistentHighlightPulseMode =
  (typeof PersistentHighlightPulseModes)[keyof typeof PersistentHighlightPulseModes];
export type PersistentHighlightThicknessMode =
  (typeof PersistentHighlightThicknessModes)[keyof typeof PersistentHighlightThicknessModes];

export interface PersistentHighlightData {
  readonly enabled: boolean;
  readonly semantic: PersistentHighlightSemantic;
  /** Normalized `#RRGGBB`, or `null` when the semantic color is used. */
  readonly colorHex: string | null;
  readonly pulseMode: PersistentHighlightPulseMode;
  /** Positive cycle count, or `null` for infinite (persisted as `-1`). */
  readonly pulseCount: number | null;
  readonly thicknessMode: PersistentHighlightThicknessMode;
  /** Web-only: one pulse cycle in ms, or `null` for the design default. */
  readonly pulseMs: number | null;
  /** Web-only: core outline width in CSS px, or `null` for the thickness preset. */
  readonly outlineWidth: number | null;
  /** Web-only: inner-glow reach in CSS px, or `null` for the thickness preset. */
  readonly glowReach: number | null;
}

export type Rgb = readonly [number, number, number];

// Design handoff "Part Highlight (outline + inner glow)", 2026-09-15. Its README
// states these numbers are the spec; the prototype page's own 2200 ms is not.
export const HIGHLIGHT_DEFAULT_PULSE_MS = 2800;
export const HIGHLIGHT_PULSE_MS_RANGE = { min: 1200, max: 4000 } as const;
export const HIGHLIGHT_OUTLINE_WIDTH_RANGE = { min: 1.5, max: 8 } as const;
export const HIGHLIGHT_GLOW_REACH_RANGE = { min: 4, max: 24 } as const;
/** Pulsing ↔ steady, and a highlight's fade-in: "280ms — no jump". */
export const HIGHLIGHT_TRANSITION_SECONDS = 0.28;

const K_GLOW_OPACITY_TROUGH = 0.2;
const K_GLOW_OPACITY_PEAK = 0.72;
const K_GLOW_OPACITY_STEADY = 0.55;
const K_OUTLINE_OPACITY_TROUGH = 0.8;
const K_OUTLINE_OPACITY_PEAK = 1;
const K_OUTLINE_OPACITY_STEADY = 1;

/** Unity `PersistentHighlightData` default instance. */
export const DEFAULT_PERSISTENT_HIGHLIGHT: PersistentHighlightData = {
  enabled: true,
  semantic: PersistentHighlightSemantics.Attention,
  colorHex: null,
  pulseMode: PersistentHighlightPulseModes.Continuous,
  pulseCount: null,
  thicknessMode: PersistentHighlightThicknessModes.Thick,
  pulseMs: null,
  outlineWidth: null,
  glowReach: null,
};

// -- Normalization -----------------------------------------------------------

/** Unity's alias folding: trim, lowercase, strip `_`, `-` and spaces. */
function fold(value: string): string {
  return value.trim().toLowerCase().replace(/[-_ ]/g, "");
}

export function normalizeHighlightSemantic(value: unknown): PersistentHighlightSemantic {
  if (typeof value !== "string" || !value.trim()) {
    return PersistentHighlightSemantics.Attention;
  }
  switch (fold(value)) {
    case "attention":
    case "focus":
    case "focused":
    case "current":
    case "currentfocus":
    case "yellow":
      return PersistentHighlightSemantics.Attention;
    case "correct":
    case "completed":
    case "complete":
    case "success":
    case "green":
      return PersistentHighlightSemantics.Correct;
    case "hazard":
    case "error":
    case "danger":
    case "warning":
    case "red":
      return PersistentHighlightSemantics.Hazard;
    default:
      return PersistentHighlightSemantics.Attention;
  }
}

export function normalizeHighlightPulseMode(value: unknown): PersistentHighlightPulseMode {
  if (typeof value !== "string" || !value.trim()) {
    return PersistentHighlightPulseModes.Continuous;
  }
  switch (fold(value)) {
    case "single":
    case "singlesoft":
    case "soft":
    case "once":
      return PersistentHighlightPulseModes.SingleSoft;
    case "continuous":
    case "loop":
    case "looping":
    case "infinite":
    case "infinitely":
    case "forever":
    case "pulse":
    case "pulsing":
      return PersistentHighlightPulseModes.Continuous;
    case "none":
    case "off":
    case "steady":
    case "static":
      return PersistentHighlightPulseModes.None;
    default:
      return PersistentHighlightPulseModes.Continuous;
  }
}

export function normalizeHighlightThicknessMode(
  value: unknown,
): PersistentHighlightThicknessMode {
  if (typeof value !== "string" || !value.trim()) {
    return PersistentHighlightThicknessModes.Thick;
  }
  switch (fold(value)) {
    case "medium":
    case "mid":
    case "moderate":
      return PersistentHighlightThicknessModes.Medium;
    case "thick":
    case "heavy":
    case "heavier":
    case "bold":
    case "strong":
    case "default":
    case "normal":
    case "regular":
    case "standard":
    case "max":
    case "maximum":
      return PersistentHighlightThicknessModes.Thick;
    case "thin":
    case "thinner":
    case "subtle":
    case "light":
    case "slim":
    case "narrow":
      return PersistentHighlightThicknessModes.Thin;
    default:
      return PersistentHighlightThicknessModes.Thick;
  }
}

/** Unity `NormalizePulseCount`: anything `<= 0` (or absent) means infinite. */
export function normalizeHighlightPulseCount(value: unknown): number | null {
  const count = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(count)) return null;
  const whole = Math.trunc(count);
  return whole > 0 ? whole : null;
}

export function normalizeHighlightColorHex(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = tryParseHtmlColor(value) ?? tryParseHtmlColor(`#${value.trim()}`);
  return parsed ? toHtmlStringRgb(parsed) : null;
}

/**
 * A web-only numeric knob: absent, non-numeric or `<= 0` means "not set"
 * (`null`, so the default applies); anything else is clamped into the range
 * the design allows rather than refused.
 */
function normalizeOptionalRange(
  value: unknown,
  range: { readonly min: number; readonly max: number },
): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.min(range.max, Math.max(range.min, numeric));
}

export function normalizeHighlightPulseMs(value: unknown): number | null {
  const ms = normalizeOptionalRange(value, HIGHLIGHT_PULSE_MS_RANGE);
  return ms === null ? null : Math.round(ms);
}

export function normalizeHighlightOutlineWidth(value: unknown): number | null {
  return normalizeOptionalRange(value, HIGHLIGHT_OUTLINE_WIDTH_RANGE);
}

export function normalizeHighlightGlowReach(value: unknown): number | null {
  return normalizeOptionalRange(value, HIGHLIGHT_GLOW_REACH_RANGE);
}

/** Unity `NormalizeInPlace` over a partial/untrusted shape (+ the web fields). */
export function normalizeHighlight(
  value: Partial<PersistentHighlightData>,
): PersistentHighlightData {
  return {
    enabled: value.enabled !== false,
    semantic: normalizeHighlightSemantic(value.semantic),
    colorHex: normalizeHighlightColorHex(value.colorHex),
    pulseMode: normalizeHighlightPulseMode(value.pulseMode),
    pulseCount: normalizeHighlightPulseCount(value.pulseCount),
    thicknessMode: normalizeHighlightThicknessMode(value.thicknessMode),
    pulseMs: normalizeHighlightPulseMs(value.pulseMs),
    outlineWidth: normalizeHighlightOutlineWidth(value.outlineWidth),
    glowReach: normalizeHighlightGlowReach(value.glowReach),
  };
}

// -- Serialization -----------------------------------------------------------

/** Unity `PersistentHighlightData.Deserialize` — null for blank/invalid/disabled. */
export function parsePersistentHighlightJson(
  json: string | null | undefined,
): PersistentHighlightData | null {
  if (!json || !json.trim()) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  // Unity reads `enabled` off a `[SerializeField] bool` that defaults to true,
  // so a payload that omits it is enabled — but an explicit `false` is not.
  if (record.enabled === false) return null;
  return normalizeHighlight(record as Partial<PersistentHighlightData>);
}

/**
 * Unity `PersistentHighlightData.Serialize` — null when disabled (the caller
 * clears the attribute). Field order and the `""` / `-1` encodings match
 * JsonUtility's output so a Unity peer reads web-written rows unchanged.
 */
export function serializePersistentHighlight(
  highlight: PersistentHighlightData | null,
): string | null {
  if (!highlight || !highlight.enabled) return null;
  const normalized = normalizeHighlight(highlight);
  const payload: Record<string, string | number | boolean> = {
    enabled: true,
    semantic: normalized.semantic,
    colorHex: normalized.colorHex ?? "",
    pulseMode: normalized.pulseMode,
    pulseCount: normalized.pulseCount ?? -1,
    thicknessMode: normalized.thicknessMode,
  };
  // Web-only knobs go LAST and only when set, so a highlight that uses none of
  // them is byte-identical to what JsonUtility writes (see the file header).
  if (normalized.pulseMs !== null) payload.pulseMs = normalized.pulseMs;
  if (normalized.outlineWidth !== null) payload.outlineWidth = normalized.outlineWidth;
  if (normalized.glowReach !== null) payload.glowReach = normalized.glowReach;
  return JSON.stringify(payload);
}

/** Unity `ProjectModel_Entities.PersistentHighlightsEqual` (+ the web fields). */
export function persistentHighlightsEqual(
  left: PersistentHighlightData | null,
  right: PersistentHighlightData | null,
): boolean {
  if (!left || !right) return !left && !right;
  return left.enabled === right.enabled
    && left.semantic === right.semantic
    && left.colorHex === right.colorHex
    && left.pulseMode === right.pulseMode
    && left.pulseCount === right.pulseCount
    && left.thicknessMode === right.thicknessMode
    && left.pulseMs === right.pulseMs
    && left.outlineWidth === right.outlineWidth
    && left.glowReach === right.glowReach;
}

// -- Derived properties ------------------------------------------------------

export function usesCustomHighlightColor(highlight: PersistentHighlightData): boolean {
  return !!highlight.colorHex;
}

export function shouldPlayHighlightEntryPulse(highlight: PersistentHighlightData): boolean {
  return highlight.pulseMode === PersistentHighlightPulseModes.SingleSoft;
}

export function shouldPulseHighlightContinuously(highlight: PersistentHighlightData): boolean {
  return highlight.pulseMode === PersistentHighlightPulseModes.Continuous;
}

/**
 * Unity `ResolveColor` — custom hex wins, else the semantic colour. The
 * semantic palette is the design's tokens, not Unity's `Color32` (header).
 */
export function resolveHighlightColor(highlight: PersistentHighlightData): Rgb {
  if (highlight.colorHex) {
    const parsed = tryParseHtmlColor(highlight.colorHex);
    if (parsed) return parsed;
  }
  switch (highlight.semantic) {
    case PersistentHighlightSemantics.Correct:
      return hexTripletToRgb("2e9e54"); // highlight/success
    case PersistentHighlightSemantics.Hazard:
      return hexTripletToRgb("f84536"); // highlight/alert (Campfire ember)
    default:
      return hexTripletToRgb("ffd233"); // highlight/default
  }
}

/** What a highlight looks like, with every web knob resolved to a number. */
export interface HighlightStyle {
  readonly color: Rgb;
  readonly pulseSeconds: number;
  /** Core outline width, CSS px (constant in screen space). */
  readonly outlineWidthPx: number;
  /** Inner-glow reach inward from the silhouette, CSS px. */
  readonly glowReachPx: number;
}

/**
 * `thicknessMode` keeps meaning something: `thick` (the default) is the design's
 * 2.5 px / 12 px, and the lighter modes step down inside the design's ranges.
 * An explicit `outlineWidth` / `glowReach` wins over the preset.
 */
export function resolveHighlightStyle(highlight: PersistentHighlightData): HighlightStyle {
  let presetOutline = 2.5;
  let presetGlow = 12;
  if (highlight.thicknessMode === PersistentHighlightThicknessModes.Medium) {
    presetOutline = 2;
    presetGlow = 9;
  } else if (highlight.thicknessMode === PersistentHighlightThicknessModes.Thin) {
    presetOutline = 1.5;
    presetGlow = 6;
  }
  return {
    color: resolveHighlightColor(highlight),
    pulseSeconds: (highlight.pulseMs ?? HIGHLIGHT_DEFAULT_PULSE_MS) / 1000,
    outlineWidthPx: highlight.outlineWidth ?? presetOutline,
    glowReachPx: highlight.glowReach ?? presetGlow,
  };
}

// -- AI scene-context labels -------------------------------------------------

/** Unity `GetContextColorLabel`. */
export function highlightContextColorLabel(highlight: PersistentHighlightData): string {
  if (usesCustomHighlightColor(highlight)) {
    return toHtmlStringRgb(resolveHighlightColor(highlight));
  }
  switch (highlight.semantic) {
    case PersistentHighlightSemantics.Correct:
      return "green";
    case PersistentHighlightSemantics.Hazard:
      return "red";
    default:
      return "yellow";
  }
}

/** Unity `GetContextPulseLabel`. */
export function highlightContextPulseLabel(highlight: PersistentHighlightData): string {
  if (highlight.pulseMode === PersistentHighlightPulseModes.None) return "static";
  if (highlight.pulseMode === PersistentHighlightPulseModes.Continuous) {
    if (highlight.pulseCount === null) return "pulsing";
    if (highlight.pulseCount === 1) return "pulse once";
    if (highlight.pulseCount === 2) return "pulse twice";
    return `pulse ${highlight.pulseCount} times`;
  }
  return "pulse once";
}

/** Unity `GetContextThicknessLabel`. */
export function highlightContextThicknessLabel(highlight: PersistentHighlightData): string {
  return highlight.thicknessMode;
}

// -- Visual state ------------------------------------------------------------

/** `cubic-bezier(.4, 0, .4, 1)` — the design's symmetric ease, "never springy". */
export const easeHighlight = cubicBezierTiming(0.4, 0, 0.4, 1);

/**
 * Pulse level 0..1 at a phase measured in CYCLES (any real number). CSS applies
 * a keyframe's timing function PER SEGMENT, so the prototype's
 * `0%,100% {.2} 50% {.72}` eases up across the first half and back down across
 * the second — not one curve over the whole cycle.
 */
export function highlightPulseLevel(phaseCycles: number): number {
  const phase = phaseCycles - Math.floor(phaseCycles);
  return phase < 0.5 ? easeHighlight(phase * 2) : 1 - easeHighlight((phase - 0.5) * 2);
}

/**
 * The core outline's opacity at pulse `level` (0 trough → 1 peak: 80 → 100 %),
 * crossfaded toward steady (100 %) by `steadyWeight` (0 pulsing → 1 steady).
 * Plain numbers rather than a pair object, so a caller animating every frame
 * allocates nothing.
 */
export function highlightOutlineOpacity(level: number, steadyWeight: number): number {
  const pulse = K_OUTLINE_OPACITY_TROUGH
    + (K_OUTLINE_OPACITY_PEAK - K_OUTLINE_OPACITY_TROUGH) * clamp01(level);
  return pulse + (K_OUTLINE_OPACITY_STEADY - pulse) * clamp01(steadyWeight);
}

/** The inner glow's opacity: 20 → 72 % over the pulse, 55 % when steady. */
export function highlightGlowOpacity(level: number, steadyWeight: number): number {
  const pulse = K_GLOW_OPACITY_TROUGH + (K_GLOW_OPACITY_PEAK - K_GLOW_OPACITY_TROUGH) * clamp01(level);
  return pulse + (K_GLOW_OPACITY_STEADY - pulse) * clamp01(steadyWeight);
}

export function isInfiniteHighlightPulseActive(highlight: PersistentHighlightData): boolean {
  return shouldPulseHighlightContinuously(highlight) && highlight.pulseCount === null;
}

/**
 * How long a `pulseCount` highlight runs before it clears itself — `pulseCount`
 * whole cycles of ITS pulse length — or `null` when it never does.
 */
export function highlightFinitePulseDurationSeconds(
  highlight: PersistentHighlightData,
): number | null {
  if (!shouldPulseHighlightContinuously(highlight) || highlight.pulseCount === null) {
    return null;
  }
  return (highlight.pulseCount * (highlight.pulseMs ?? HIGHLIGHT_DEFAULT_PULSE_MS)) / 1000;
}

export function isFiniteHighlightPulseActive(
  highlight: PersistentHighlightData,
  elapsedSeconds: number,
): boolean {
  const duration = highlightFinitePulseDurationSeconds(highlight);
  return duration !== null && elapsedSeconds < duration;
}

/** Unity `HasFinitePulseAnimationExpired` — the auto-clear trigger. */
export function hasFiniteHighlightPulseExpired(
  highlight: PersistentHighlightData,
  elapsedSeconds: number,
): boolean {
  const duration = highlightFinitePulseDurationSeconds(highlight);
  return duration !== null && elapsedSeconds >= duration;
}

// -- Color parsing (Unity `ColorUtility`) ------------------------------------

/**
 * Unity's named-color table (`ColorUtility.TryParseHtmlString` accepts these
 * case-insensitively alongside `#RGB` / `#RGBA` / `#RRGGBB` / `#RRGGBBAA`).
 * Kept because Unity accepts `colorHex: "red"` and the normalizer must fold it
 * to `#FF0000` on both clients.
 */
const K_NAMED_COLORS: Readonly<Record<string, string>> = {
  red: "ff0000",
  cyan: "00ffff",
  blue: "0000ff",
  darkblue: "0000a0",
  lightblue: "add8e6",
  purple: "800080",
  yellow: "ffff00",
  lime: "00ff00",
  fuchsia: "ff00ff",
  white: "ffffff",
  silver: "c0c0c0",
  grey: "808080",
  gray: "808080",
  black: "000000",
  orange: "ffa500",
  brown: "a52a2a",
  maroon: "800000",
  green: "008000",
  olive: "808000",
  navy: "000080",
  teal: "008080",
  aqua: "00ffff",
  magenta: "ff00ff",
};

/** Unity `ColorUtility.TryParseHtmlString`; alpha is parsed and then dropped. */
function tryParseHtmlColor(value: string): Rgb | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const named = K_NAMED_COLORS[trimmed.toLowerCase()];
  if (named) return hexTripletToRgb(named);

  if (!trimmed.startsWith("#")) return null;
  const digits = trimmed.slice(1).toLowerCase();
  if (!/^[0-9a-f]+$/.test(digits)) return null;
  if (digits.length === 3 || digits.length === 4) {
    return hexTripletToRgb(
      digits
        .slice(0, 3)
        .split("")
        .map((c) => c + c)
        .join(""),
    );
  }
  if (digits.length === 6 || digits.length === 8) return hexTripletToRgb(digits.slice(0, 6));
  return null;
}

function hexTripletToRgb(digits: string): Rgb {
  return [
    parseInt(digits.slice(0, 2), 16) / 255,
    parseInt(digits.slice(2, 4), 16) / 255,
    parseInt(digits.slice(4, 6), 16) / 255,
  ];
}

/** Unity `ColorUtility.ToHtmlStringRGB` — uppercase, `#`-prefixed, no alpha. */
export function toHtmlStringRgb(color: Rgb): string {
  const byte = (value: number) =>
    Math.min(255, Math.max(0, Math.round(value * 255)))
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return `#${byte(color[0])}${byte(color[1])}${byte(color[2])}`;
}

// -- Math --------------------------------------------------------------------

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * A CSS `cubic-bezier(x1, y1, x2, y2)` timing function: solve the curve's x for
 * the input progress (Newton, then bisection where the slope is flat), return
 * its y. Endpoints are exact.
 */
function cubicBezierTiming(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number): number => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const error = sampleX(t) - x;
      if (Math.abs(error) < 1e-7) return sampleY(t);
      const slope = slopeX(t);
      if (Math.abs(slope) < 1e-7) break;
      t -= error / slope;
    }
    let low = 0;
    let high = 1;
    t = x;
    for (let i = 0; i < 40; i++) {
      const value = sampleX(t);
      if (Math.abs(value - x) < 1e-7) break;
      if (value < x) low = t;
      else high = t;
      t = (low + high) / 2;
    }
    return sampleY(t);
  };
}
